import type { Word } from "../types";

/**
 * The vocabulary bank — common spoken-Hindi words a heritage speaker already
 * knows, ordered roughly by the earliest lesson that can spell them.
 *
 * Every word's `devanagari` is NFC-normalized. `iast` is a practical
 * transliteration (schwa-deletion applied where Hindi drops the final 'a', e.g.
 * घर → ghar, not ghara). `gloss` is a short English meaning.
 *
 * Which lesson a word may appear in is governed by `isReadable` in
 * `lessons.ts` and enforced by `content-lint.test.ts`; do not assume — the test
 * decides.
 */
export const words: Word[] = [
  // ── l04: ka-varga (क ख ग घ ङ) + vowels, inherent-a only ─────────────────
  { id: "w-ek", devanagari: "एक", iast: "ek", gloss: "one" },
  { id: "w-aag", devanagari: "आग", iast: "āg", gloss: "fire" },
  {
    id: "w-ang",
    devanagari: "अंग",
    iast: "aṅg",
    gloss: "limb, part of the body",
  },
  { id: "w-ank", devanagari: "अंक", iast: "aṅk", gloss: "number, digit" },
  { id: "w-ug", devanagari: "उग", iast: "ug", gloss: "sprout, grow (stem)" },
  { id: "w-khag", devanagari: "खग", iast: "khag", gloss: "bird" },

  // ── l05: ca-varga (च छ ज झ ञ) ───────────────────────────────────────────
  { id: "w-jag", devanagari: "जग", iast: "jag", gloss: "world" },
  { id: "w-gaj", devanagari: "गज", iast: "gaj", gloss: "elephant" },
  { id: "w-aaj", devanagari: "आज", iast: "āj", gloss: "today" },
  { id: "w-jaj", devanagari: "जज", iast: "jaj", gloss: "judge" },
  { id: "w-gajak", devanagari: "गजक", iast: "gajak", gloss: "sesame brittle" },
  { id: "w-cakh", devanagari: "चख", iast: "cakh", gloss: "taste (stem)" },

  // ── l06: tta-varga retroflex (ट ठ ड ढ ण) ────────────────────────────────
  { id: "w-ghat", devanagari: "घट", iast: "ghaṭ", gloss: "pot, waterpot" },
  { id: "w-thag", devanagari: "ठग", iast: "ṭhag", gloss: "swindler, cheat" },
  { id: "w-kan", devanagari: "कण", iast: "kaṇ", gloss: "particle, grain" },
  { id: "w-dank", devanagari: "डंक", iast: "ḍaṅk", gloss: "sting" },
  { id: "w-atak", devanagari: "अटक", iast: "aṭak", gloss: "get stuck (stem)" },
  {
    id: "w-jhatak",
    devanagari: "झटक",
    iast: "jhaṭak",
    gloss: "jerk, yank (stem)",
  },

  // ── l07: ta-varga dental (त थ द ध न) ────────────────────────────────────
  { id: "w-tan", devanagari: "तन", iast: "tan", gloss: "body" },
  { id: "w-dhan", devanagari: "धन", iast: "dhan", gloss: "wealth" },
  { id: "w-jan", devanagari: "जन", iast: "jan", gloss: "people, person" },
  { id: "w-ghan", devanagari: "घन", iast: "ghan", gloss: "dense; a cube" },
  { id: "w-gagan", devanagari: "गगन", iast: "gagan", gloss: "sky" },
  { id: "w-nath", devanagari: "नथ", iast: "nath", gloss: "nose-ring" },
  { id: "w-oon", devanagari: "ऊन", iast: "ūn", gloss: "wool" },
  {
    id: "w-ainak",
    devanagari: "ऐनक",
    iast: "ainak",
    gloss: "spectacles, glasses",
  },

  // ── l08: pa-varga (प फ ब भ म) ───────────────────────────────────────────
  { id: "w-man", devanagari: "मन", iast: "man", gloss: "mind, heart" },
  { id: "w-namak", devanagari: "नमक", iast: "namak", gloss: "salt" },
  {
    id: "w-naman",
    devanagari: "नमन",
    iast: "naman",
    gloss: "a bow, salutation",
  },
  {
    id: "w-bhajan",
    devanagari: "भजन",
    iast: "bhajan",
    gloss: "devotional song",
  },
  { id: "w-chamak", devanagari: "चमक", iast: "camak", gloss: "shine, sparkle" },
  { id: "w-path", devanagari: "पथ", iast: "path", gloss: "path" },
  { id: "w-aam", devanagari: "आम", iast: "ām", gloss: "mango" },

  // ── l09: antastha (य र ल व) ─────────────────────────────────────────────
  { id: "w-ghar", devanagari: "घर", iast: "ghar", gloss: "house, home" },
  { id: "w-jal", devanagari: "जल", iast: "jal", gloss: "water" },
  { id: "w-phal", devanagari: "फल", iast: "phal", gloss: "fruit" },
  { id: "w-kamal", devanagari: "कमल", iast: "kamal", gloss: "lotus" },
  { id: "w-garam", devanagari: "गरम", iast: "garam", gloss: "hot, warm" },
  { id: "w-magar", devanagari: "मगर", iast: "magar", gloss: "but" },
  { id: "w-matar", devanagari: "मटर", iast: "maṭar", gloss: "peas" },
  { id: "w-bandar", devanagari: "बंदर", iast: "bandar", gloss: "monkey" },
  { id: "w-aurat", devanagari: "औरत", iast: "aurat", gloss: "woman" },

  // ── l10: ushma (श ष स ह) ────────────────────────────────────────────────
  { id: "w-hum", devanagari: "हम", iast: "ham", gloss: "we" },
  { id: "w-sab", devanagari: "सब", iast: "sab", gloss: "all, everyone" },
  { id: "w-bas", devanagari: "बस", iast: "bas", gloss: "enough; bus" },
  { id: "w-bahan", devanagari: "बहन", iast: "bahan", gloss: "sister" },
  { id: "w-shahar", devanagari: "शहर", iast: "śahar", gloss: "city" },
  { id: "w-mahal", devanagari: "महल", iast: "mahal", gloss: "palace" },
  { id: "w-das", devanagari: "दस", iast: "das", gloss: "ten" },
  { id: "w-jagah", devanagari: "जगह", iast: "jagah", gloss: "place" },
  { id: "w-os", devanagari: "ओस", iast: "os", gloss: "dew" },

  // ── l11: consonant review (inherent-a only) ─────────────────────────────
  { id: "w-kal", devanagari: "कल", iast: "kal", gloss: "yesterday, tomorrow" },
  { id: "w-hal", devanagari: "हल", iast: "hal", gloss: "solution; plough" },
  { id: "w-naram", devanagari: "नरम", iast: "naram", gloss: "soft" },
  { id: "w-baras", devanagari: "बरस", iast: "baras", gloss: "year; to rain" },
  { id: "w-lahar", devanagari: "लहर", iast: "lahar", gloss: "wave" },
  {
    id: "w-bartan",
    devanagari: "बरतन",
    iast: "bartan",
    gloss: "utensil, vessel",
  },

  // ── l12: matras 1 (ा ि ी) ───────────────────────────────────────────────
  { id: "w-naam", devanagari: "नाम", iast: "nām", gloss: "name" },
  { id: "w-kaam", devanagari: "काम", iast: "kām", gloss: "work" },
  { id: "w-paani", devanagari: "पानी", iast: "pānī", gloss: "water" },
  { id: "w-din", devanagari: "दिन", iast: "din", gloss: "day" },
  { id: "w-raat", devanagari: "रात", iast: "rāt", gloss: "night" },
  { id: "w-baat", devanagari: "बात", iast: "bāt", gloss: "talk, matter" },
  { id: "w-haath", devanagari: "हाथ", iast: "hāth", gloss: "hand" },
  { id: "w-maata", devanagari: "माता", iast: "mātā", gloss: "mother" },
  { id: "w-pita", devanagari: "पिता", iast: "pitā", gloss: "father" },
  {
    id: "w-dadi",
    devanagari: "दादी",
    iast: "dādī",
    gloss: "grandmother (paternal)",
  },
  {
    id: "w-nani",
    devanagari: "नानी",
    iast: "nānī",
    gloss: "grandmother (maternal)",
  },
  { id: "w-saal", devanagari: "साल", iast: "sāl", gloss: "year" },
  { id: "w-imli", devanagari: "इमली", iast: "imlī", gloss: "tamarind" },
  { id: "w-rishi", devanagari: "ऋषि", iast: "ṛṣi", gloss: "sage, seer" },
  {
    id: "w-chacha",
    devanagari: "चाचा",
    iast: "cācā",
    gloss: "uncle (father's brother)",
  },
  { id: "w-bhai", devanagari: "भाई", iast: "bhāī", gloss: "brother" },
  { id: "w-shaam", devanagari: "शाम", iast: "śām", gloss: "evening" },
  { id: "w-naak", devanagari: "नाक", iast: "nāk", gloss: "nose" },
  { id: "w-kaan", devanagari: "कान", iast: "kān", gloss: "ear" },
  { id: "w-daal", devanagari: "दाल", iast: "dāl", gloss: "lentils" },
  { id: "w-jana", devanagari: "जाना", iast: "jānā", gloss: "to go" },
  { id: "w-aana", devanagari: "आना", iast: "ānā", gloss: "to come" },
  { id: "w-hawa", devanagari: "हवा", iast: "havā", gloss: "air, wind" },
  { id: "w-kamra", devanagari: "कमरा", iast: "kamrā", gloss: "room" },

  // ── l13: matras 2 (ु ू े ै) ─────────────────────────────────────────────
  { id: "w-phul", devanagari: "फूल", iast: "phūl", gloss: "flower" },
  { id: "w-guru", devanagari: "गुरु", iast: "guru", gloss: "teacher, guru" },
  { id: "w-doodh", devanagari: "दूध", iast: "dūdh", gloss: "milk" },
  { id: "w-mez", devanagari: "मेज", iast: "mez", gloss: "table" },
  { id: "w-desh", devanagari: "देश", iast: "deś", gloss: "country" },
  { id: "w-paisa", devanagari: "पैसा", iast: "paisā", gloss: "money" },
  { id: "w-subah", devanagari: "सुबह", iast: "subah", gloss: "morning" },
  { id: "w-bahut", devanagari: "बहुत", iast: "bahut", gloss: "very, a lot" },
  { id: "w-beta", devanagari: "बेटा", iast: "beṭā", gloss: "son" },
  { id: "w-beti", devanagari: "बेटी", iast: "beṭī", gloss: "daughter" },
  { id: "w-tel", devanagari: "तेल", iast: "tel", gloss: "oil" },
  { id: "w-pair", devanagari: "पैर", iast: "pair", gloss: "foot, leg" },
  { id: "w-seb", devanagari: "सेब", iast: "seb", gloss: "apple" },
  { id: "w-aalu", devanagari: "आलू", iast: "ālū", gloss: "potato" },
  { id: "w-dena", devanagari: "देना", iast: "denā", gloss: "to give" },
  { id: "w-lena", devanagari: "लेना", iast: "lenā", gloss: "to take" },
  { id: "w-sundar", devanagari: "सुंदर", iast: "sundar", gloss: "beautiful" },

  // ── l14: matras 3 (ो ौ ृ) & chandrabindu (ँ) ────────────────────────────
  { id: "w-log", devanagari: "लोग", iast: "log", gloss: "people" },
  { id: "w-mor", devanagari: "मोर", iast: "mor", gloss: "peacock" },
  { id: "w-kaun", devanagari: "कौन", iast: "kaun", gloss: "who" },
  { id: "w-mausam", devanagari: "मौसम", iast: "mausam", gloss: "weather" },
  { id: "w-chand", devanagari: "चाँद", iast: "cāṁd", gloss: "moon" },
  { id: "w-gaon", devanagari: "गाँव", iast: "gāṁv", gloss: "village" },
  { id: "w-maa", devanagari: "माँ", iast: "māṁ", gloss: "mother, mom" },
  { id: "w-daant", devanagari: "दाँत", iast: "dāṁt", gloss: "tooth" },
  { id: "w-paanch", devanagari: "पाँच", iast: "pāṁc", gloss: "five" },
  {
    id: "w-kripa",
    devanagari: "कृपा",
    iast: "kṛpā",
    gloss: "kindness; please",
  },
  { id: "w-sona", devanagari: "सोना", iast: "sonā", gloss: "gold; to sleep" },
  { id: "w-aankh", devanagari: "आँख", iast: "āṁkh", gloss: "eye" },

  // ── l15: barakhadi (matra-rich review) ──────────────────────────────────
  { id: "w-kitab", devanagari: "किताब", iast: "kitāb", gloss: "book" },
  { id: "w-roti", devanagari: "रोटी", iast: "roṭī", gloss: "flatbread, roti" },
  { id: "w-khana", devanagari: "खाना", iast: "khānā", gloss: "food; to eat" },
  { id: "w-kahani", devanagari: "कहानी", iast: "kahānī", gloss: "story" },
  { id: "w-mahina", devanagari: "महीना", iast: "mahīnā", gloss: "month" },
  { id: "w-bagicha", devanagari: "बगीचा", iast: "bagīcā", gloss: "garden" },
  { id: "w-sapna", devanagari: "सपना", iast: "sapnā", gloss: "dream" },
  { id: "w-duniya", devanagari: "दुनिया", iast: "duniyā", gloss: "world" },
  { id: "w-karna", devanagari: "करना", iast: "karnā", gloss: "to do" },
  { id: "w-pina", devanagari: "पीना", iast: "pīnā", gloss: "to drink" },
  { id: "w-mandir", devanagari: "मंदिर", iast: "mandir", gloss: "temple" },
  { id: "w-gaajar", devanagari: "गाजर", iast: "gājar", gloss: "carrot" },

  // ── l16: numerals (number-words) ────────────────────────────────────────
  { id: "w-do", devanagari: "दो", iast: "do", gloss: "two" },
  { id: "w-teen", devanagari: "तीन", iast: "tīn", gloss: "three" },
  { id: "w-char", devanagari: "चार", iast: "cār", gloss: "four" },
  { id: "w-chhah", devanagari: "छह", iast: "chah", gloss: "six" },
  { id: "w-saat", devanagari: "सात", iast: "sāt", gloss: "seven" },
  { id: "w-aath", devanagari: "आठ", iast: "āṭh", gloss: "eight" },

  // ── l17: halant / joined consonants ─────────────────────────────────────
  {
    id: "w-namaste",
    devanagari: "नमस्ते",
    iast: "namaste",
    gloss: "hello, greetings",
  },
  {
    id: "w-hindi",
    devanagari: "हिन्दी",
    iast: "hindī",
    gloss: "Hindi (the language)",
  },
  { id: "w-dost", devanagari: "दोस्त", iast: "dost", gloss: "friend" },
  { id: "w-hasta", devanagari: "हस्त", iast: "hast", gloss: "hand (formal)" },
  { id: "w-vakt", devanagari: "वक्त", iast: "vakt", gloss: "time" },
  {
    id: "w-shukriya",
    devanagari: "शुक्रिया",
    iast: "śukriyā",
    gloss: "thank you",
  },
  {
    id: "w-ank-conj",
    devanagari: "अङ्क",
    iast: "aṅk",
    gloss: "number (phonetic spelling with ṅ)",
  },
  { id: "w-satya", devanagari: "सत्य", iast: "satya", gloss: "truth" },
  { id: "w-bhakt", devanagari: "भक्त", iast: "bhakt", gloss: "devotee" },
  {
    id: "w-shakti",
    devanagari: "शक्ति",
    iast: "śakti",
    gloss: "power, strength",
  },

  // ── l18: common conjuncts (क्ष त्र ज्ञ श्र) ──────────────────────────────
  {
    id: "w-kshama",
    devanagari: "क्षमा",
    iast: "kṣamā",
    gloss: "forgiveness, pardon",
  },
  { id: "w-chhatra", devanagari: "छात्र", iast: "chātr", gloss: "student" },
  { id: "w-gyan", devanagari: "ज्ञान", iast: "gyān", gloss: "knowledge" },
  { id: "w-shram", devanagari: "श्रम", iast: "śram", gloss: "labour, effort" },
  { id: "w-mitra", devanagari: "मित्र", iast: "mitr", gloss: "friend" },
  { id: "w-rakshak", devanagari: "रक्षक", iast: "rakṣak", gloss: "protector" },

  // ── l19: forms of ra (प्र, र्म, र्ष …) ──────────────────────────────────
  { id: "w-pyar", devanagari: "प्यार", iast: "pyār", gloss: "love" },
  { id: "w-prem", devanagari: "प्रेम", iast: "prem", gloss: "love (formal)" },
  {
    id: "w-karm",
    devanagari: "कर्म",
    iast: "karm",
    gloss: "action, deed, karma",
  },
  { id: "w-dharm", devanagari: "धर्म", iast: "dharm", gloss: "religion, duty" },
  { id: "w-varsha", devanagari: "वर्षा", iast: "varṣā", gloss: "rain" },
  { id: "w-parv", devanagari: "पर्व", iast: "parv", gloss: "festival" },
  { id: "w-surya", devanagari: "सूर्य", iast: "sūrya", gloss: "sun" },
  { id: "w-garv", devanagari: "गर्व", iast: "garv", gloss: "pride" },

  // ── l20: gemination (doubled consonants) ────────────────────────────────
  { id: "w-achchha", devanagari: "अच्छा", iast: "acchā", gloss: "good, okay" },
  { id: "w-billi", devanagari: "बिल्ली", iast: "billī", gloss: "cat" },
  {
    id: "w-rassi",
    devanagari: "रस्सी",
    iast: "rassī",
    gloss: "rope, string",
  },
  {
    id: "w-gadda",
    devanagari: "गद्दा",
    iast: "gaddā",
    gloss: "mattress",
  },
  { id: "w-bachcha", devanagari: "बच्चा", iast: "baccā", gloss: "child" },
  { id: "w-kutta", devanagari: "कुत्ता", iast: "kuttā", gloss: "dog" },
  {
    id: "w-pakka",
    devanagari: "पक्का",
    iast: "pakkā",
    gloss: "firm, ripe, certain",
  },
  {
    id: "w-sachcha",
    devanagari: "सच्चा",
    iast: "saccā",
    gloss: "true, honest",
  },
  { id: "w-gussa", devanagari: "गुस्सा", iast: "gussā", gloss: "anger" },
  { id: "w-dibba", devanagari: "डिब्बा", iast: "ḍibbā", gloss: "box, tin" },

  // ── l21: nukta (ज़ फ़ ड़ ढ़ …) ───────────────────────────────────────────
  { id: "w-sadak", devanagari: "सड़क", iast: "saṛak", gloss: "road, street" },
  { id: "w-bada", devanagari: "बड़ा", iast: "baṛā", gloss: "big" },
  {
    id: "w-padhna",
    devanagari: "पढ़ना",
    iast: "paṛhnā",
    gloss: "to read, to study",
  },
  { id: "w-ladka", devanagari: "लड़का", iast: "laṛkā", gloss: "boy" },
  { id: "w-zindagi", devanagari: "ज़िंदगी", iast: "zindagī", gloss: "life" },
  { id: "w-bazaar", devanagari: "बाज़ार", iast: "bāzār", gloss: "market" },
  { id: "w-maza", devanagari: "मज़ा", iast: "mazā", gloss: "fun, enjoyment" },
  { id: "w-safar", devanagari: "सफ़र", iast: "safar", gloss: "journey" },
  { id: "w-tez", devanagari: "तेज़", iast: "tez", gloss: "fast, sharp" },
  {
    id: "w-namaz",
    devanagari: "नमाज़",
    iast: "namāz",
    gloss: "prayer (namaz)",
  },
  { id: "w-phone", devanagari: "फ़ोन", iast: "fon", gloss: "phone" },
  { id: "w-daftar", devanagari: "दफ़्तर", iast: "daftar", gloss: "office" },
  { id: "w-qila", devanagari: "क़िला", iast: "qilā", gloss: "fort" },
  { id: "w-khabar", devanagari: "ख़बर", iast: "khabar", gloss: "news" },
  { id: "w-gham", devanagari: "ग़म", iast: "gham", gloss: "grief, sorrow" },
];

/** Every word indexed by its stable id. */
export const wordById: ReadonlyMap<string, Word> = new Map(
  words.map((word) => [word.id, word]),
);
